import { TrendingUp, Clock, Shield, Target, Gauge } from 'lucide-react'
import { useState, useEffect } from 'react'
import { landingStatsApi } from '@/services/api'
import type { IndustryStats } from '@/types'

export default function StatsSection() {
  const [stats, setStats] = useState<IndustryStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        const response = await landingStatsApi.getLandingStats()
        // Merge API data with fallback values for new fields
        setStats({
          ...response.industryStats,
          systemUptime: response.industryStats.systemUptime || '99.9%',
          systemUptimeChange: response.industryStats.systemUptimeChange || '+0.2%',
          dataAccuracy: response.industryStats.dataAccuracy || '99.8%',
          dataAccuracyChange: response.industryStats.dataAccuracyChange || '+1.1%',
          performanceScore: response.industryStats.performanceScore || '98.5',
          performanceScoreChange: response.industryStats.performanceScoreChange || '+2.1%',
        })
      } catch (err) {
        console.error('Error fetching landing stats:', err)
        // Fallback to demo data if API fails
        setStats({
          activeUsers: 10000,
          activeUsersChange: '+12%',
          apiIntegrations: 50,
          apiIntegrationsChange: '+8',
          loadsProcessed: '2.4M+',
          loadsProcessedChange: '+23%',
          avgResponseTime: '<500ms',
          responseTimeChange: '-15%',
          systemUptime: '99.9%',
          systemUptimeChange: '+0.2%',
          dataAccuracy: '99.8%',
          dataAccuracyChange: '+1.1%',
          performanceScore: '98.5',
          performanceScoreChange: '+2.1%',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading) {
    return (
      <section className="w-full py-16 md:py-24 lg:py-32 bg-gradient-to-br from-neutral-50 to-primary-50/30">
        <div className="container px-4 md:px-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </section>
    )
  }

  const statItems = stats ? [
    {
      title: 'Average Response Time',
      value: stats.avgResponseTime,
      description: 'Lightning-fast data aggregation',
      icon: Clock,
      change: stats.responseTimeChange,
    },
    {
      title: 'System Uptime',
      value: stats.systemUptime,
      description: 'Reliable 24/7 platform availability',
      icon: Shield,
      change: stats.systemUptimeChange,
    },
    {
      title: 'Data Accuracy',
      value: stats.dataAccuracy,
      description: 'Precision in freight data processing',
      icon: Target,
      change: stats.dataAccuracyChange,
    },
    {
      title: 'Performance Score',
      value: stats.performanceScore,
      description: 'Overall platform efficiency rating',
      icon: Gauge,
      change: stats.performanceScoreChange,
    },
  ] : []

  return (
    <section className="w-full py-16 md:py-24 lg:py-32 bg-gradient-to-br from-neutral-50 to-primary-50/30">
      <div className="container px-4 md:px-6">
        <div className="flex flex-col items-center justify-center space-y-8 text-center">
          <div className="space-y-6">
            <div className="inline-flex items-center rounded-full border px-6 py-3 text-sm bg-white/80 backdrop-blur-sm border-primary-200 text-primary-700">
              <TrendingUp className="mr-2 h-4 w-4" />
              Platform Performance
            </div>
            <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl bg-gradient-to-r from-primary-900 to-accent-700 bg-clip-text text-transparent">
              Trusted by Industry Leaders
            </h2>
            <p className="max-w-[800px] text-neutral-600 text-lg md:text-xl leading-relaxed">
              AIBoard is powering the next generation of logistics operations worldwide.
            </p>
          </div>
        </div>
        <div className="mx-auto grid max-w-6xl items-center gap-8 py-12 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {statItems.map((stat, index) => {
            const IconComponent = stat.icon
            return (
              <div key={index} className="group flex flex-col items-center space-y-4 text-center p-8 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 hover:bg-white/80 hover:shadow-xl transition-all duration-300 hover:scale-105">
                <div className="relative">
                  <div className="rounded-full bg-gradient-to-br from-primary-100 to-primary-200 p-4 group-hover:from-primary-200 group-hover:to-primary-300 transition-all duration-300">
                    <IconComponent className="h-8 w-8 text-primary-600" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center">
                    <span className={`text-xs font-bold text-white ${
                      stat.change?.startsWith('+') ? '' : 'text-accent-100'
                    }`}>
                      {stat.change?.startsWith('+') ? '+' : '-'}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-primary-700 to-accent-600 bg-clip-text text-transparent">{stat.value}</h3>
                  <p className="font-semibold text-neutral-800">{stat.title}</p>
                  <p className="text-sm text-neutral-600">{stat.description}</p>
                  <div className="flex items-center justify-center space-x-1 pt-2">
                    <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                      stat.change?.startsWith('+') 
                        ? 'text-success-700 bg-success-100' 
                        : 'text-accent-700 bg-accent-100'
                    }`}>
                      {stat.change}
                    </span>
                    <span className="text-xs text-neutral-500">vs last month</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
} 